import {
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  debounce
} from 'lodash';
import React from 'react';
import LogoAshby from '../../../assets/images/logo__ashby.png';
import {
  NOT,
  YES
} from '../../../lib/Boolean.lib';
import {
  ID__ATS_TYPE__ASHBY_API
} from '../../../lib/Definition';
import {
  Fun
} from '../../../lib/Function.lib';
import useState from '../../../lib/hooks/useState.hook';
import {
  getPersonName,
  Obj
} from '../../../lib/Object.lib';
import Ashby from '../../../lib/services/ATS/Ashby.lib';
import getAtsContextProperty from '../../../lib/services/ATS/getAtsContextProperty.fun';
import getAtsContextUpdated from '../../../lib/services/ATS/getAtsContextUpdated.fun';
import {
  showConfirm
} from '../../Dialogs/AppConfirmationDialog';
import {
  THEME__COLOR__ERROR,
  THEME__COLOR__SUCCESS
} from '../../Layout/Libraries/Theme.lib';
import Box from '../../Layout/Wrappers/Box';
import Button from '../../Layout/Wrappers/Button';
import Chip from '../../Layout/Wrappers/Chip';
import Fieldset from '../../Layout/Wrappers/Fieldset';
import Once from '../../Layout/Wrappers/Once';
import TextField from '../../Layout/Wrappers/TextField';
import Typography from '../../Layout/Wrappers/Typography';

const VALUE__ATS__USER__EMAIL = 'recruiter@10by10.io';

export default function SubSectionAtsSettingsAshbyEmployer({
  acl = true,
  entity = {},
  onChange = Fun,
  ...props
}) {

  const [state, updateState] = useState();
  const refFieldsetApiKey = React.useRef({});
  const entityRef = React.useRef(entity);
  React.useEffect(() => {
    entityRef.current = entity;
  }, [entity]);
  if (NOT(acl)) { return null; }

  const atsTypeId = ID__ATS_TYPE__ASHBY_API;

  // apiKey
  const VALUE__ATS__API_KEY = getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'apiKey'
  });

  // userId
  const VALUE__ATS__USER__ID = getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'userId'
  });

  const fetchAtsUser = async () => {
    await updateState({
      busyUser: true
    });
    // apiKey
    const VALUE__ATS__API_KEY = getAtsContextProperty({
      entity: entityRef.current,
      atsTypeId,
      property: 'apiKey'
    });
    const user = Obj(
      await Ashby.searchUser({
        apiKey: VALUE__ATS__API_KEY,
        userEmail: VALUE__ATS__USER__EMAIL,
      })
    );
    await updateState({
      busyUser: false
    });
    await onChange(
      getAtsContextUpdated({
        entity: entityRef.current,
        atsTypeId,
        update: {
          userId: user.id,
          userName: getPersonName(user),
          userGlobalRole: user.globalRole
        }
      })
    );
  }

  const onChangeTextField = (field, callback = Fun) => {
    return async (event, value) => {
      const prevValue = getAtsContextProperty({
        entity,
        atsTypeId,
        field
      });
      await onChange(
        getAtsContextUpdated({
          entity,
          atsTypeId,
          update: {
            [field]: value
          }
        })
      );
      if (value === prevValue) { return; }
      debounce(callback)({ prevValue, value });
    }
  }

  const onClickEditApiKey = () => {
    function FieldsetApiKey({
      apiKey: propsApiKey = ''
    }) {
      const [state, updateState] = useState({ apiKey: propsApiKey });
      refFieldsetApiKey.current = { state };
      console.debug('FieldsetApiKey', refFieldsetApiKey.current);
      return (
        <Fieldset
          title='API key'
          subtitle='Authentication credential to ATS.'
        >
          <TextField disabledBlurExport
            name="_atsApiKey"
            value={state.apiKey}
            onChange={(event) => updateState({ apiKey: event.target.value })}
          />
        </Fieldset>
      );
    }
    showConfirm({
      title: 'Ashby Integration',
      content: (
        <FieldsetApiKey />
      ),
      onAcceptLabel: 'Connect',
      onAccept: async (event) => {
        const value = refFieldsetApiKey.current.state?.apiKey;
        console.debug('onAccept', value);
        onChangeTextField('apiKey', fetchAtsUser)(event, value);
      },
      paperStyle: {
        width: sizes.m7
      }
    });
  }

  const onClickDisconnect = () => {
    showConfirm({
      title: 'Ashby Integration',
      message: (
        <Box column>
          <Typography className='c-black-dark f-md fw-400'>
            Disconnecting 10 By 10's active API integration will delay candidate submissions and event processing
          </Typography>
          <br />
          <Typography className='c-black-dark f-md fw-400'>
            Are you sure you want to proceed?
          </Typography>
        </Box>
      ),
      onAcceptLabel: 'Disconnect',
      onAccept: async () => {
        await onChange(
          getAtsContextUpdated({
            entity,
            atsTypeId,
            update: {
              apiKey: '',
              sourceId: '',
              userId: '',
              userName: '',
              userGlobalRole: ''
            }
          })
        );
      }
    });
  }

  const YES__CONNECTED = YES(VALUE__ATS__USER__ID);
  const NOT__CONNECTED = NOT(YES__CONNECTED);

  const LOADER = <CircularProgress className='icon16 mr-1' />;

  return (
    <Box column flex1 {...props}>

      <Box row
        className='p-1'
      >
        <Box
          className='p-05 outlined rounded mr-1 bg-white'
        >
          <Avatar
            src={LogoAshby}
            variant='rounded'
          />
        </Box>
        <Box column flex1>
          <Box row flex1>
            <Typography className='fw-500 f-md mr-1'>
              Ashby
            </Typography>
            <Chip slim
              acl={YES__CONNECTED}
              color={THEME__COLOR__SUCCESS}
            >
              Connected
            </Chip>
            <Chip slim
              acl={NOT__CONNECTED}
              color={THEME__COLOR__ERROR}
            >
              Disconnected
            </Chip>
            {state.busyUser && LOADER}
            <Once
              acl={VALUE__ATS__API_KEY}
              promise={fetchAtsUser}
              render={false}
            />
          </Box>
          <Typography className='fw-400 f-sm'>
            Applicant Tracking System
          </Typography>
        </Box>
      </Box>

      <Box row
        className='p-1 align-right'
      >
        <Button outlined
          acl={YES__CONNECTED}
          label='Disconnect API'
          onClick={onClickDisconnect}
          className='min-w-160 mr-1'
        />
        <Button primary
          label='Edit API key'
          onClick={onClickEditApiKey}
          className='min-w-160'
        />
      </Box>

    </Box>
  );
}
